import { useEffect, useMemo, useState } from "react"
import { Column, FieldType, FieldValue, Section, Table } from "../../../../features/ui"
import { Operation } from "../../../operations"
import { Lot, Product, ProductName, useLotLoaders, useProductLoaders } from "../../../products"
import { DeliveryMethod } from "../../state/types"
import { isDefined } from "../../../../utils/typeUtils"

type Props = {
    deliveryMethod: DeliveryMethod
    items: {
        itemId: string
        quantity?: number
        pending?: number
        dispatched?: number
        delivered?: number
        cancelled?: number
        returned?: number
        notDelivered?: number
        defective?: number
        nonDefective?: number
    }[]
    operation: Operation
    title?: string
}

export const DeliveryStatsTable = (props: Props) => {
    const { deliveryMethod, items, operation, title } = props

    const [products, setProducts] = useState<Product[]>([])
    const [lots, setLots] = useState<Lot[]>([])

    const productLoaders = useProductLoaders()
    const lotLoaders = useLotLoaders()

    const load = async () => {
        const operationItems = operation.items.filter(item => items.some(deliveryItem => deliveryItem.itemId === item.id))
        const { loadProducts } = productLoaders()
        const loadedProducts = await loadProducts(operationItems.map(item => item.productId))
        setProducts(loadedProducts)
        const { loadProductsLots } = lotLoaders()
        const loadedLots = await loadProductsLots(loadedProducts.map(product => product.id))
        setLots(loadedLots)
    }

    useEffect(() => {
        load()
    }, [operation.id, items])

    const showQuantityColumn = useMemo(() => items.some(item => isDefined(item.quantity)), [items])
    const showPendingColumn = useMemo(() => items.some(item => isDefined(item.pending)), [items])
    const showDispatchedColumn = useMemo(() => deliveryMethod === DeliveryMethod.SHIPMENT && items.some(item => isDefined(item.dispatched)), [items, deliveryMethod])
    const showDeliveredColumn = useMemo(() => items.some(item => isDefined(item.delivered)), [items])
    const showCancelledColumn = useMemo(() => items.some(item => (item.cancelled || 0) > 0), [items])
    const showReturnedColumn = useMemo(() => items.some(item => (item.returned || 0) > 0), [items])
    const showNotDeliveredColumn = useMemo(() => items.some(item => (item.notDelivered || 0) > 0), [items])
    const showDefectiveColumns = useMemo(() => items.some(item => isDefined(item.defective)), [items])

    const columns: Column[] = useMemo(() => {
        const columnList: Column[] = [
            {
                name: 'itemId',
                type: FieldType.TEXT,
                label: '',
                render: (value?: FieldValue) => {
                    const operationItem = operation.items.find(item => item.id === value)
                    const product = products.find(item => item.id === operationItem?.productId)
                    const lot = lots.find(item => item.id === operationItem?.lotId)
                    return <ProductName product={product} lot={lot} showSupplierCode />
                }
            }
        ]
        if (showDeliveredColumn) {
            columnList.splice(1, 0, {
                name: 'delivered',
                type: FieldType.NUMBER,
                label: 'Entregados'
            })
        }
        if (showDispatchedColumn) {
            columnList.splice(1, 0, {
                name: 'dispatched',
                type: FieldType.NUMBER,
                label: 'Despachados'
            })
        }
        if (showPendingColumn) {
            columnList.splice(1, 0, {
                name: 'pending',
                type: FieldType.NUMBER,
                label: 'Pendientes'
            })
        }
        if (showQuantityColumn) {
            columnList.splice(1, 0, {
                name: 'quantity',
                type: FieldType.NUMBER,
                label: 'Cantidad'
            })
        }
        if (showCancelledColumn) {
            columnList.push({
                name: 'cancelled',
                type: FieldType.NUMBER,
                label: 'Cancelados'
            })
        }
        if (showReturnedColumn) {
            columnList.push({
                name: 'returned',
                type: FieldType.NUMBER,
                label: 'Devueltos'
            })
        }
        if (showDefectiveColumns) {
            columnList.push({
                name: 'defective',
                type: FieldType.NUMBER,
                label: 'Defectuosos'
            }, {
                name: 'nonDefective',
                type: FieldType.NUMBER,
                label: 'Sin Defecto'
            })
        }
        if (showNotDeliveredColumn) {
            columnList.push({
                name: 'notDelivered',
                type: FieldType.NUMBER,
                label: 'No Entregados'
            })
        }
        return columnList
    }, [items, operation.items, products, lots, showCancelledColumn, showReturnedColumn, showNotDeliveredColumn, showDefectiveColumns, deliveryMethod])

    return (
        <Section
            id="delivery-stats"
            title={title || 'Productos a Entregar'}
            space={12}
        >
            <Table
                columns={columns}
                rows={items}
                rowId="itemId"
                readOnly
                alignRight={false}
            />
        </Section>
    )
}
